import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, message, Alert, Button } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getSignedUrl } from 'store/commonSlice';
import { searchVendorV2 } from 'store/vendorSliceV2';
import {
	searchVehicleTypesV2,
	updateVehicleV2,
	searchVehiclesV2,
	transferVehicleV2,
	replaceVehicleV2,
} from 'store/vehicleSliceV2';
import TransferVehicleModal from '../edit-vehiclev2/TransferVehicleModal';
import ReplaceVehicleModal from '../edit-vehiclev2/ReplaceVehicleModal';
import DocumentViewerModal from 'componentsv2/common/modals/DocumentViewerModal';
import VehicleDetailsForm from './VehicleDetailsForm';
import {
	PageContainer,
	ContentWrapper,
	Header,
	HeaderContent,
	HeaderTitle,
	HeaderSubtitle,
	HeaderActions,
	ActionButton,
	TransferButton,
	// ReplaceButton, // disabled: vehicle replacement moved to trip level
	BackButton,
	ContractFooter,
	ViewContractButton,
} from './VehicleDetailsPageV2.styles';

// Constants
const DATE_FORMAT = 'YYYY-MM-DD';

const toMomentOrNull = (value) => {
	if (!value) return null;
	const parsedValue = moment(value, [moment.ISO_8601, DATE_FORMAT], true);
	return parsedValue.isValid() ? parsedValue : null;
};

export default function VehicleDetailsPageV2() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	// States
	const [loading, setLoading] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [vehicleData, setVehicleData] = useState(location.state?.vehicle || null);
	const [vendorOptions, setVendorOptions] = useState([]);
	const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
	const [skipDocumentUpload, setSkipDocumentUpload] = useState(false);

	// File upload refs
	const rcDocFileInputRef = useRef(null);
	const insuranceDocFileInputRef = useRef(null);
	const pucDocFileInputRef = useRef(null);
	const consentFileInputRef = useRef(null);

	// File upload states
	const [uploading, setUploading] = useState({
		rc_doc: false,
		insurance: false,
		puc_doc: false,
		consent_letter: false,
	});

	// File keys
	const [fileKeys, setFileKeys] = useState({
		rc_doc_key: null,
		insurance_key: null,
		puc_doc_key: null,
		consent_letter_key: null,
	});

	// File names for display
	const [fileNames, setFileNames] = useState({
		rc_doc: null,
		insurance: null,
		puc_doc: null,
		consent_letter: null,
	});

	// Transfer modal state
	const [transferModalVisible, setTransferModalVisible] = useState(false);
	// Replace modal state
	const [replaceModalVisible, setReplaceModalVisible] = useState(false);

	// Document viewer modal state
	const [documentViewerVisible, setDocumentViewerVisible] = useState(false);
	const [viewingDocumentUrl, setViewingDocumentUrl] = useState('');
	const [viewingDocumentName, setViewingDocumentName] = useState('');
	const [viewingDocumentType, setViewingDocumentType] = useState('');
	const hasSavedMandatoryDocuments = Boolean(
		vehicleData?.document_info?.rc?.document_key &&
			vehicleData?.document_info?.insurance?.document_key
	);
	const hasPendingMandatoryDocuments = Boolean(vehicleData) && !hasSavedMandatoryDocuments;

	// Watch ownership type for consent letter button
	const ownershipType = Form.useWatch('ownership_type', form);

	// Helper function to extract filename from S3 key by removing UUID prefix
	// S3 keys are in format: {uuid}-{filename} or path/{uuid}-{filename}
	// Example: "4c93e939-78c5-4323-a82b-e3c8b5bce97a-Screenshot from 2026-02-21 14-57-38.png"
	// Returns: "Screenshot from 2026-02-21 14-57-38.png"
	const extractFileName = (key) => {
		if (!key) return null;

		// Get the last part of the path (filename with UUID)
		const filenameWithPath = key.split('/').pop();

		// Remove UUID prefix if present (UUID format: 8-4-4-4-12 characters)
		const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
		const match = filenameWithPath.match(uuidPattern);

		if (match) {
			// Return filename without UUID prefix
			return filenameWithPath.substring(match[0].length);
		}

		// If no UUID pattern found, return the original filename
		return filenameWithPath;
	};

	// Fetch vendors
	const fetchVendors = async () => {
		try {
			const response = await dispatch(
				searchVendorV2({
					pagination: { page: 1, per_page: 1000 },
				})
			);

			if (response && response.rows && Array.isArray(response.rows)) {
				const options = response.rows
					.filter((item) => item && item.id && item.vendor_name)
					.map((item) => ({
						value: item.id,
						label: item.vendor_name,
					}));
				setVendorOptions(options);
			}
		} catch (error) {
			message.error('Failed to fetch vendors');
		}
	};

	// Fetch vehicle types
	const fetchVehicleTypes = async () => {
		try {
			const response = await dispatch(
				searchVehicleTypesV2({
					pagination: { page: 1, per_page: 1000 },
				})
			);

			if (response && response.rows && Array.isArray(response.rows)) {
				const options = response.rows
					.filter((item) => item && item.id && item.vehicle_type_name)
					.map((item) => ({
						value: item.id,
						label: item.vehicle_type_name,
					}));
				setVehicleTypeOptions(options);
			}
		} catch (error) {
			message.error('Failed to fetch vehicle types');
		}
	};

	// Fetch vehicle data
	const fetchVehicleData = async () => {
		// If vehicle data is already passed through location state, use it
		if (location.state?.vehicle) {
			initializeFormData(location.state.vehicle);
			navigate(location.pathname, { replace: true, state: {} });
			return;
		}

		// Otherwise, fetch from API using searchVehiclesV2 with the ID
		setLoading(true);
		try {
			const response = await dispatch(
				searchVehiclesV2({
					pagination: { page: 1, per_page: 1 },
					filter_by: { vehicle_number: id },
				})
			);

			if (response?.rows?.length > 0) {
				setVehicleData(response.rows[0]);
				initializeFormData(response.rows[0]);
			} else {
				message.error('Vehicle not found');
				navigate('/vehicles');
			}
		} catch (error) {
			message.error('Failed to fetch vehicle details');
			navigate('/vehicles');
		} finally {
			setLoading(false);
		}
	};

	// Initialize form data
	const initializeFormData = (data) => {
		// Extract document info from nested structure
		const documentInfo = data.document_info || {};
		const rcDoc = documentInfo.rc || {};
		const insuranceDoc = documentInfo.insurance || {};
		const pucDoc = documentInfo.puc || {};
		const consentDoc = documentInfo.consent_letter || {};

		const hasUploadedMandatoryDocuments = Boolean(
			rcDoc.document_key && insuranceDoc.document_key
		);
		const isDocSkipped =
			!hasUploadedMandatoryDocuments &&
			(data.documents_complete === false ||
				data.skip_document_upload ||
				(!rcDoc.document_key && !insuranceDoc.document_key));
		setSkipDocumentUpload(isDocSkipped);

		form.setFieldsValue({
			vendor_id: data.vendor_id,
			vehicle_number: data.vehicle_number,
			vehicle_type_id: data.vehicle_type_id,
			rc_doc_key: rcDoc.document_key,
			rc_expiry_date: toMomentOrNull(rcDoc.valid_till),
			insurance_key: insuranceDoc.document_key,
			insurance_validity_date: toMomentOrNull(insuranceDoc.valid_till),
			puc_doc_key: pucDoc.document_key,
			puc_expiry_date: toMomentOrNull(pucDoc.valid_till),
			consent_letter_key: consentDoc.document_key,
			ownership_type: data.ownership_type,
			gps_installed: data.gps_installed,
			gps_provider_name: data.gps_provider_name,
		});

		setFileKeys({
			rc_doc_key: rcDoc.document_key,
			insurance_key: insuranceDoc.document_key,
			puc_doc_key: pucDoc.document_key,
			consent_letter_key: consentDoc.document_key,
		});

		setFileNames({
			rc_doc: extractFileName(rcDoc.document_key),
			insurance: extractFileName(insuranceDoc.document_key),
			puc_doc: extractFileName(pucDoc.document_key),
			consent_letter: extractFileName(consentDoc.document_key),
		});
	};

	// Handle file upload
	const handleFileUpload = async (file, fieldName) => {
		if (!file) {
			message.error('Please select a file');
			return false;
		}

		const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
		const maxSizeMB = 5;

		if (!allowedTypes.includes(file.type)) {
			message.error('You can only upload PDF, PNG, JPG, JPEG files!');
			return false;
		}
		if (file.size / 1024 / 1024 > maxSizeMB) {
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return false;
		}

		setFileNames((prev) => ({ ...prev, [fieldName]: file.name }));
		setUploading((prev) => ({ ...prev, [fieldName]: true }));

		try {
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vendor',
						keys: [file.name],
					},
				})
			);

			// Handle both response patterns (with status check and without)
			// Use array destructuring to get first item
			const [fileData] = signedUrlResponse?.data || [];

			// Use filename (with UUID) as that's what S3 expects and what we need to store
			// The response may also have a 'key' field, but 'filename' is the actual S3 key
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL - missing URL or filename');
			}

			// Upload file to S3
			const uploadResponse = await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(`Upload failed with status: ${uploadResponse.status}`);
			}

			// Update both state and form values
			const keyField = `${fieldName}_key`;
			setFileKeys((prev) => ({ ...prev, [keyField]: fileData.filename }));
			form.setFieldsValue({ [keyField]: fileData.filename });

			// Update file name for display (use original file name, not the S3 key)
			setFileNames((prev) => ({ ...prev, [fieldName]: file.name }));

			message.success('File uploaded successfully');
			return true;
		} catch (error) {
			message.error(`Failed to upload file: ${error.message || 'Unknown error'}`);
			setFileNames((prev) => ({ ...prev, [fieldName]: null }));
			// Clear the file key from state on error
			const keyField = `${fieldName}_key`;
			setFileKeys((prev) => ({ ...prev, [keyField]: null }));
			form.setFieldsValue({ [keyField]: null });
			return false;
		} finally {
			setUploading((prev) => ({ ...prev, [fieldName]: false }));
		}
	};

	// Handle file input change
	const handleFileChange = async (event, fieldName) => {
		const file = event.target.files?.[0];
		if (file) {
			await handleFileUpload(file, fieldName);
		}
		if (event.target) {
			event.target.value = '';
		}
	};

	// Handle browse file click
	const handleBrowseFile = (fieldName, ref) => {
		ref.current?.click();
	};

	// Handle file removal
	const handleRemoveFile = (fieldName) => {
		const keyField = `${fieldName}_key`;
		setFileKeys((prev) => ({ ...prev, [keyField]: null }));
		setFileNames((prev) => ({ ...prev, [fieldName]: null }));

		// Clear related expiry date when removing a doc so stale validation errors don't linger
		const expiryFieldMap = {
			rc_doc: 'rc_expiry_date',
			insurance: 'insurance_validity_date',
			puc_doc: 'puc_expiry_date',
		};
		const expiryField = expiryFieldMap[fieldName];
		const formUpdates = { [keyField]: null };
		if (expiryField) {
			formUpdates[expiryField] = null;
		}
		form.setFieldsValue(formUpdates);

		// Explicitly clear any displayed validation errors on the cleared fields
		const fieldsToClearErrors = [{ name: keyField, errors: [] }];
		if (expiryField) {
			fieldsToClearErrors.push({ name: expiryField, errors: [] });
		}
		form.setFields(fieldsToClearErrors);

		// Reset file input ref
		const refMap = {
			rc_doc: rcDocFileInputRef,
			insurance: insuranceDocFileInputRef,
			puc_doc: pucDocFileInputRef,
			consent_letter: consentFileInputRef,
		};
		const ref = refMap[fieldName];
		if (ref?.current) {
			ref.current.value = '';
		}

		message.success('File removed');
	};

	// Handle viewing document
	const handleViewDocument = async (documentKey, documentName = 'Document') => {
		if (!documentKey) {
			message.error('No document key provided');
			return;
		}

		try {
			// Clean document key - remove S3 path prefix if present
			let cleanKey = documentKey;
			if (cleanKey.startsWith('s3://')) {
				// Remove s3://bucket/ prefix
				cleanKey = cleanKey.replace(/^s3:\/\/[^/]+\//, '');
			}
			// Remove any leading slashes
			cleanKey = cleanKey.replace(/^\/+/, '');

			// Get signed URL for viewing - try 'download' first (matches EditVehicleModal)
			const response = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'vendor',
						keys: [cleanKey],
					},
				})
			);

			// Check response - handle both patterns (EditVehicleModal and CreateVehiclePageV2)
			// Pattern 1: Status-based response (response.status === 'success')
			// Pattern 2: Direct data response (response.data exists)
			const documentUrl =
				(response && response.status === 'success' && response.data?.[0]?.url) ||
				(response?.data &&
					Array.isArray(response.data) &&
					response.data.length > 0 &&
					response.data[0]?.url)
					? response.data[0].url
					: null;

			if (documentUrl) {
				// Detect document type from file extension
				const extension = cleanKey.split('.').pop()?.toLowerCase();
				let docType = 'unknown';
				const lowerKey = cleanKey.toLowerCase();
				if (lowerKey.includes('.pdf') || extension === 'pdf') {
					docType = 'pdf';
				} else if (
					lowerKey.includes('.jpg') ||
					lowerKey.includes('.jpeg') ||
					lowerKey.includes('.png') ||
					['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)
				) {
					docType = 'image';
				}

				setViewingDocumentUrl(documentUrl);
				setViewingDocumentName(documentName);
				setViewingDocumentType(docType);
				setDocumentViewerVisible(true);
			} else {
				message.error('Failed to load document - Invalid response');
			}
		} catch (error) {
			message.error(`Failed to view document: ${error.message || 'Unknown error'}`);
		}
	};

	// Handle closing document viewer
	const handleCloseDocumentViewer = () => {
		setDocumentViewerVisible(false);
		setViewingDocumentUrl('');
		setViewingDocumentName('');
		setViewingDocumentType('');
	};

	const buildDocumentInfo = (values, keys, formatDate) => {
		const rcDocKey = values.rc_doc_key || keys.rc_doc_key;
		const insuranceKey = values.insurance_key || keys.insurance_key;
		const pucDocKey = values.puc_doc_key || keys.puc_doc_key;
		const consentLetterKey = values.consent_letter_key || keys.consent_letter_key;

		if (!rcDocKey && !insuranceKey && !pucDocKey && !consentLetterKey) {
			return null;
		}

		const documentInfo = {};
		if (rcDocKey) {
			documentInfo.rc = {
				document_key: rcDocKey,
				valid_till: formatDate(values.rc_expiry_date),
			};
		}
		if (insuranceKey) {
			documentInfo.insurance = {
				document_key: insuranceKey,
				valid_till: formatDate(values.insurance_validity_date),
			};
		}
		if (pucDocKey) {
			documentInfo.puc = {
				document_key: pucDocKey,
				valid_till: formatDate(values.puc_expiry_date),
			};
		}
		if (consentLetterKey) {
			documentInfo.consent_letter = {
				document_key: consentLetterKey,
			};
		}
		return documentInfo;
	};

	// Build payload for vehicle update
	const buildUpdatePayload = (values) => {
		const formatDate = (date) => (date ? date.format(DATE_FORMAT) : null);
		const documentInfo = skipDocumentUpload
			? null
			: buildDocumentInfo(values, fileKeys, formatDate);

		const payload = {
			id: vehicleData.id,
			vendor_id: values.vendor_id,
			vehicle_number: values.vehicle_number || vehicleData.vehicle_number,
			vehicle_type_id: values.vehicle_type_id,
			ownership_type: values.ownership_type,
			gps_installed: values.gps_installed,
			gps_provider_name: values.gps_installed ? values.gps_provider_name : null,
		};

		if (documentInfo) {
			payload.document_info = documentInfo;
		}

		return payload;
	};

	// Handle successful update
	const handleUpdateSuccess = () => {
		message.success('Vehicle updated successfully');
		setIsEditMode(false);
		fetchVehicleData();
	};

	// Handle update error
	const handleUpdateError = (error) => {
		const errorMessage = error.errorFields
			? 'Please fill in all required fields'
			: 'Failed to update vehicle';
		message.error(errorMessage);
	};

	const getSubmitValidationFields = () => {
		const fields = ['vendor_id', 'vehicle_type_id', 'vehicle_number', 'gps_installed'];

		if (!skipDocumentUpload) {
			fields.push(
				'rc_doc_key',
				'rc_expiry_date',
				'insurance_key',
				'insurance_validity_date',
				'ownership_type'
			);
			if (form.getFieldValue('ownership_type') === 'Aggregator') {
				fields.push('consent_letter_key');
			}
			if (fileKeys.puc_doc_key) {
				fields.push('puc_expiry_date');
			}
		}

		if (form.getFieldValue('gps_installed') === true) {
			fields.push('gps_provider_name');
		}

		return fields;
	};

	// Handle form submission
	const handleSubmit = async () => {
		try {
			const values = await form.validateFields(getSubmitValidationFields());
			const payload = buildUpdatePayload(values);

			const response = await dispatch(updateVehicleV2({ req_data: payload }));

			if (response && response.status === 'success') {
				handleUpdateSuccess();
			} else {
				message.error(
					response?.message || response?.error?.message || 'Failed to update vehicle'
				);
			}
		} catch (error) {
			handleUpdateError(error);
		}
	};

	// Handle transfer
	const handleTransfer = () => {
		if (!vehicleData) {
			message.error('Vehicle data not loaded');
			return;
		}
		setTransferModalVisible(true);
	};

	const handleTransferConfirm = async (transferData) => {
		try {
			const response = await dispatch(
				transferVehicleV2({
					req_data: {
						vehicle_id: transferData.vehicle_id,
						to_vendor_id: transferData.to_vendor_id,
						reason: transferData.reason,
						last_working_date: transferData.last_working_date,
						start_date: transferData.start_date,
					},
				})
			);

			if (response && response.status === 'success') {
				setTransferModalVisible(false);
				navigate('/vehicles');
				return true;
			}
			if (response?.code === 'NOT_FOUND') {
				message.error('Vehicle or vendor not found');
			} else if (response?.code === 'INVALID_STATE') {
				message.error('Vehicle has active trips. Cannot transfer at this time.');
			} else if (response?.code === 'SAME_VENDOR') {
				message.error('Target vendor is the same as current vendor');
			} else {
				message.error(response?.message || 'Failed to create transfer request');
			}
			return false;
		} catch (error) {
			console.error('Error transferring vehicle:', error);
			message.error('An error occurred while creating transfer request');
			return false;
		}
	};

	// Handle replace — disabled: vehicle replacement moved to trip level.
	// const handleReplace = () => {
	// 	if (!vehicleData) {
	// 		message.error('Vehicle data not loaded');
	// 		return;
	// 	}
	// 	setReplaceModalVisible(true);
	// };

	const handleReplaceConfirm = async (replaceData) => {
		try {
			const response = await dispatch(
				replaceVehicleV2({
					req_data: {
						old_vehicle_id: replaceData.old_vehicle_id,
						new_vehicle_id: replaceData.new_vehicle_id,
						vendor_id: replaceData.vendor_id,
						reason: replaceData.reason,
						replacement_date: replaceData.replacement_date,
					},
				})
			);

			if (response && response.status === 'success') {
				setReplaceModalVisible(false);
				navigate('/vehicles');
				return true;
			}
			if (response?.code === 'NOT_FOUND') {
				message.error('Vehicle or vendor not found');
			} else if (response?.code === 'INVALID_STATE') {
				message.error('Vehicle has active trips. Cannot replace at this time.');
			} else if (response?.code === 'SAME_VEHICLE') {
				message.error('New vehicle is the same as the current vehicle');
			} else {
				message.error(response?.message || 'Failed to create replacement request');
			}
			return false;
		} catch (error) {
			console.error('Error replacing vehicle:', error);
			message.error('An error occurred while creating replacement request');
			return false;
		}
	};

	// Handle back navigation
	const handleBack = () => {
		navigate('/vehicles');
	};
	const vendorContracts = Array.isArray(vehicleData?.vendor_contracts)
		? vehicleData.vendor_contracts
		: [];
	const vendorContractId = vendorContracts[0]?.id || null;

	const handleViewVendorContract = () => {
		if (!vendorContractId) {
			message.info('No vendor contract found for this vehicle.');
			return;
		}
		navigate(`/vendor-contracts/${vendorContractId}`);
	};

	// Toggle edit mode
	const handleToggleEdit = () => {
		if (isEditMode && vehicleData) {
			form.resetFields();
			initializeFormData(vehicleData);
		} else {
			setSkipDocumentUpload(false);
		}
		setIsEditMode(!isEditMode);
	};

	const handleUploadMandatoryDocuments = () => {
		setSkipDocumentUpload(false);
		setIsEditMode(true);
	};

	// Initialize data
	useEffect(() => {
		fetchVendors();
		fetchVehicleTypes();
		fetchVehicleData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// Auto-enable edit mode if navigated with uploadDocs flag
	useEffect(() => {
		const uploadDocs =
			location.state?.uploadDocs ||
			new URLSearchParams(location.search).get('uploadDocs') === 'true';
		if (uploadDocs && hasPendingMandatoryDocuments) {
			handleUploadMandatoryDocuments();
			message.info('Please upload all mandatory documents to complete onboarding.');
		}
	}, [location, hasPendingMandatoryDocuments]);

	return (
		<PageContainer>
			<ContentWrapper>
				<Header>
					<BackButton onClick={handleBack}>←</BackButton>
					<HeaderContent>
						<HeaderTitle>Vehicle Details</HeaderTitle>
						<HeaderSubtitle>
							{isEditMode ? 'Update vehicle information' : 'View vehicle information'}
						</HeaderSubtitle>
					</HeaderContent>
					<HeaderActions>
						<TransferButton onClick={handleTransfer}>
							<svg
								width="18"
								height="18"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M20 10H4L9.5 4M4 14H20L14.5 20"
									stroke="#1990FF"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							Transfer
						</TransferButton>
						{/* Vehicle replacement moved to trip level — hidden here. */}
						{/* <ReplaceButton onClick={handleReplace}>
							<svg
								width="18"
								height="18"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
									stroke="#16a34a"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							Replace
						</ReplaceButton> */}
						{!isEditMode && (
							<ActionButton type="primary" onClick={handleToggleEdit}>
								Edit
							</ActionButton>
						)}
					</HeaderActions>
				</Header>

				{hasPendingMandatoryDocuments && !isEditMode && (
					<Alert
						message="Pending Documents"
						description="This vehicle was onboarded without documents. Please upload the required documents to complete onboarding."
						type="warning"
						showIcon
						style={{ marginBottom: 20 }}
						action={
							<Button
								size="small"
								type="primary"
								onClick={handleUploadMandatoryDocuments}
							>
								Upload Documents
							</Button>
						}
					/>
				)}

				<VehicleDetailsForm
					form={form}
					isEditMode={isEditMode}
					loading={loading}
					ownershipType={ownershipType}
					vendorOptions={vendorOptions}
					vehicleTypeOptions={vehicleTypeOptions}
					uploading={uploading}
					fileKeys={fileKeys}
					fileNames={fileNames}
					rcDocFileInputRef={rcDocFileInputRef}
					insuranceDocFileInputRef={insuranceDocFileInputRef}
					pucDocFileInputRef={pucDocFileInputRef}
					consentFileInputRef={consentFileInputRef}
					handleBrowseFile={handleBrowseFile}
					handleFileChange={handleFileChange}
					handleViewDocument={handleViewDocument}
					handleRemoveFile={handleRemoveFile}
					handleToggleEdit={handleToggleEdit}
					handleSubmit={handleSubmit}
					skipDocumentUpload={skipDocumentUpload}
				/>

				<ContractFooter>
					<ViewContractButton
						onClick={handleViewVendorContract}
						disabled={!vendorContractId}
						title={
							vendorContractId
								? 'View the vendor contract linked to this vehicle'
								: 'No vendor contract linked to this vehicle'
						}
					>
						<FileTextOutlined />
						View Vendor Contract
					</ViewContractButton>
				</ContractFooter>
			</ContentWrapper>

			{/* Transfer Vehicle Modal */}
			<TransferVehicleModal
				visible={transferModalVisible}
				vehicleData={vehicleData}
				vendorOptions={vendorOptions.filter((v) => v.value !== vehicleData?.vendor_id)}
				onConfirm={handleTransferConfirm}
				onClose={() => setTransferModalVisible(false)}
			/>

			{/* Replace Vehicle Modal */}
			<ReplaceVehicleModal
				visible={replaceModalVisible}
				vehicleData={vehicleData}
				onConfirm={handleReplaceConfirm}
				onClose={() => setReplaceModalVisible(false)}
			/>

			{/* Document Viewer Modal */}
			<DocumentViewerModal
				visible={documentViewerVisible}
				onClose={handleCloseDocumentViewer}
				documentUrl={viewingDocumentUrl}
				documentName={viewingDocumentName}
				documentType={viewingDocumentType}
			/>
		</PageContainer>
	);
}
